import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { TabularExtractionMode } from '@/services/api/cases'

export interface TableProcessingOptionsProps {
  value: TabularExtractionMode
  onChange: (value: TabularExtractionMode) => void
  disabled?: boolean
}

export function TableProcessingOptions({ value, onChange, disabled }: TableProcessingOptionsProps) {
  const { t } = useTranslation()
  const id = useId()
  return (
    <section className="space-y-3">
      <p id={`${id}-title`} className="text-sm font-medium">
        {t('structuredIngest.processing.title')}
      </p>
      <RadioGroup
        value={value}
        disabled={disabled}
        aria-labelledby={`${id}-title`}
        onValueChange={next => {
          if (next === 'structured' || next === 'llm') onChange(next)
        }}
      >
        {(['structured', 'llm'] as const).map(mode => (
          <ProcessingOption key={mode} id={`${id}-${mode}`} mode={mode} />
        ))}
      </RadioGroup>
      <p className="text-xs text-muted-foreground">{t('structuredIngest.processing.source')}</p>
    </section>
  )
}

function ProcessingOption({ id, mode }: { id: string; mode: TabularExtractionMode }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border p-3">
      <RadioGroupItem
        id={id}
        value={mode}
        aria-describedby={`${id}-description`}
        className="mt-0.5"
      />
      <div className="min-w-0 space-y-1">
        <Label htmlFor={id} className="text-sm">
          {t(`structuredIngest.processing.${mode}.title`)}
        </Label>
        <p id={`${id}-description`} className="text-xs leading-relaxed text-muted-foreground">
          {t(`structuredIngest.processing.${mode}.description`)}
        </p>
      </div>
    </div>
  )
}
